import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiCall } from '../utils/api'
import { getRecentAccounts, RecentAccount, removeRecentAccount, addRecentAccount } from '../utils/recentAccounts'
import SecurityVerificationModal from '../components/SecurityVerificationModal'
import { FlowLogo } from '../components/FlowLogo'

const Login: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const [isRegisterMode, setIsRegisterMode] = useState(searchParams.get('register') === 'true')
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    language: 'en',
    password: '',
    confirmPassword: ''
  })
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [recentAccounts, setRecentAccounts] = useState<RecentAccount[]>([])
  const [showRegularLogin, setShowRegularLogin] = useState(false)
  const [showSecurityModal, setShowSecurityModal] = useState(false)
  const [loginEmail, setLoginEmail] = useState('')
  const [sessionToken, setSessionToken] = useState<string>('')
  const [preferredMethod, setPreferredMethod] = useState<'passkey' | 'sms_2fa' | 'totp_2fa' | null>(null)
  const [quickLoginMode, setQuickLoginMode] = useState(false)
  const [quickLoginAccount, setQuickLoginAccount] = useState<RecentAccount | null>(null)
  const { login, loginWithPasskey, loginWithEmail, loading, error, clearError, supportsPasskeys, setAuthTokens } = useAuth()
  const navigate = useNavigate()


  // Load recent accounts on mount
  useEffect(() => {
    const accounts = getRecentAccounts()
    setRecentAccounts(accounts)
    
    // If register mode is requested or no recent accounts, show regular login
    if (isRegisterMode || accounts.length === 0) {
      setShowRegularLogin(true)
    }
    
    // Check for pending auth session (from previous render)
    // Only restore if session is recent (< 10 minutes old) to avoid stale session confusion
    const pendingSession = sessionStorage.getItem('pendingAuthSession')
    if (pendingSession) {
      try {
        const data = JSON.parse(pendingSession)
        const sessionAge = Date.now() - (data.createdAt || 0)
        const TEN_MINUTES = 10 * 60 * 1000
        
        if (data.createdAt && sessionAge < TEN_MINUTES) {
          // Session is still fresh - restore it
          setSessionToken(data.sessionToken)
          setLoginEmail(data.userEmail)
          setShowSecurityModal(true)
        } else {
          // Session is stale or has no timestamp - clear it
          sessionStorage.removeItem('pendingAuthSession')
        }
      } catch (err) {
        sessionStorage.removeItem('pendingAuthSession')
      }
    }
  }, [isRegisterMode])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setValidationErrors([])
  }

  const validateRegistration = (): boolean => {
    const errors: string[] = []

    if (formData.firstName.length < 2) {
      errors.push(t('validation.required'))
    }
    if (!/^[a-zA-Z\s'-]+$/.test(formData.firstName)) {
      errors.push('First name can only contain letters, spaces, hyphens, and apostrophes')
    }
    if (formData.lastName.length < 2) {
      errors.push(t('validation.required'))
    }
    if (!/^[a-zA-Z\s'-]+$/.test(formData.lastName)) {
      errors.push('Last name can only contain letters, spaces, hyphens, and apostrophes')
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push(t('validation.invalidEmail'))
    }
    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone)) {
      errors.push(t('validation.invalidPhone'))
    }
    if (formData.password.length < 6) {
      errors.push(t('validation.passwordTooShort'))
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.push('Password must contain at least one lowercase letter, one uppercase letter, and one number')
    }
    if (formData.password !== formData.confirmPassword) {
      errors.push(t('validation.passwordsDoNotMatch'))
    }

    setValidationErrors(errors)
    return errors.length === 0
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    if (!validateRegistration()) {
      return
    }

    try {
      const response = await apiCall('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          language: formData.language,
          password: formData.password
        })
      })

      const data = await response.json()

      if (response.ok) {
        // Registration successful, automatically log in
        const result = await login(formData.email, formData.password)
        
        // New users should see security setup modal
        if (result !== null && result.requiresVerification) {
          // Has existing security features - verify
          sessionStorage.setItem('pendingAuthSession', JSON.stringify({
            sessionToken: result.sessionToken,
            has2FA: result.has2FA,
            hasPasskeys: result.hasPasskeys,
            userEmail: formData.email,
            createdAt: Date.now()
          }))
          setSessionToken(result.sessionToken)
          setLoginEmail(formData.email)
          setShowSecurityModal(true)
        } else if (result !== null && (result as any).needsSecuritySetup) {
          // Needs to set up security
          setLoginEmail(formData.email)
          setShowSecurityModal(true)
        } else {
          // No security required
          navigate('/dashboard')
        }
      } else {
        setValidationErrors([data.message || t('messages.savingError')])
      }
    } catch (err) {
      setValidationErrors([t('messages.networkError')])
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    
    try {
      const result = await login(formData.email, formData.password)
      
      // Check if backend requires additional verification (2FA/passkey)
      if (result !== null && result.requiresVerification) {
        // Store session token and show security modal
        setPreferredMethod((result as any).preferredQuickLoginMethod || null)
        
        // Store in sessionStorage to survive re-renders
        sessionStorage.setItem('pendingAuthSession', JSON.stringify({
          sessionToken: result.sessionToken,
          has2FA: result.has2FA,
          hasSMS2FA: result.hasSMS2FA,
          hasPasskeys: result.hasPasskeys,
          userEmail: formData.email,
          createdAt: Date.now()
        }))
        
        setSessionToken(result.sessionToken)
        setLoginEmail(formData.email)
        setShowSecurityModal(true)
      } else if (result !== null && (result as any).needsSecuritySetup) {
        // User is authenticated but needs to set up security
        setLoginEmail(formData.email)
        setShowSecurityModal(true)
      } else {
        // Legacy flow: login complete, no security setup needed
        navigate('/dashboard')
      }
    } catch (err) {
      // Error will be handled by AuthContext
    }
  }

  const handleSecurityVerified = (token?: string, refreshToken?: string, user?: any, verificationMethod?: 'passkey' | 'sms_2fa' | 'totp_2fa') => {
    // Clear pending auth session from sessionStorage
    sessionStorage.removeItem('pendingAuthSession')
    
    // If tokens are provided, this was a real security verification (not just setup)
    if (token && refreshToken && user) {
      // Set auth tokens in context
      if (setAuthTokens) {
        setAuthTokens(token, refreshToken, user)
      }

      // Save to recent accounts with preferred login method
      addRecentAccount({
        id: user.id.toString(),
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        profilePictureUrl: user.profile_picture_url,
        hasPasskey: verificationMethod === 'passkey',  // Legacy support
        preferredLoginMethod: verificationMethod
      })
    }
    
    setShowSecurityModal(false)
    setSessionToken('')
    navigate('/dashboard')
  }

  const handleSecuritySkip = () => {
    // Clear pending auth session from sessionStorage
    sessionStorage.removeItem('pendingAuthSession')
    
    setShowSecurityModal(false)
    navigate('/dashboard')
  }

    const handlePasskeyLogin = async () => {
    clearError()
    try {
      await loginWithPasskey()
      navigate('/dashboard')
    } catch (err: any) {
      // Silently ignore cancellation - user chose not to continue
      if (err.message === 'PASSKEY_CANCELED') {
        return
      }
      // Other errors will be handled by AuthContext
    }
  }

  const toggleMode = () => {
    setIsRegisterMode(!isRegisterMode)
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      language: 'en',
      password: '',
      confirmPassword: ''
    })
    setValidationErrors([])
    clearError()
  }

  const handleRecentAccountLogin = async (account: RecentAccount) => {
    clearError()
    
    // Use new preferredLoginMethod if available, otherwise fall back to legacy hasPasskey
    const preferredMethod = account.preferredLoginMethod || (account.hasPasskey ? 'passkey' : null)
    
    if (preferredMethod === 'passkey') {
      // Try passkey login - this works without password
      try {
        await loginWithEmail(account.email)
        navigate('/dashboard')
        return
      } catch (err: any) {
        // Silently ignore cancellation
        if (err.message === 'PASSKEY_CANCELED') {
          return
        }
        // If passkey fails, show password form as fallback
        setFormData(prev => ({ ...prev, email: account.email }))
        setShowRegularLogin(true)
        return
      }
    } else {
      // For SMS 2FA or Auth App, show quick login password screen
      setQuickLoginAccount(account)
      setQuickLoginMode(true)
      setFormData(prev => ({ ...prev, email: account.email, password: '' }))
      setPreferredMethod(preferredMethod || null)
    }
  }
  
  const handleQuickLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    
    try {
      const result = await login(formData.email, formData.password)
      
      // After password verification, the backend will trigger 2FA
      if (result !== null && result.requiresVerification) {
        setPreferredMethod((result as any).preferredQuickLoginMethod || preferredMethod)
        
        sessionStorage.setItem('pendingAuthSession', JSON.stringify({
          sessionToken: result.sessionToken,
          has2FA: result.has2FA,
          hasSMS2FA: result.hasSMS2FA,
          hasPasskeys: result.hasPasskeys,
          userEmail: formData.email,
          createdAt: Date.now()
        }))
        
        setSessionToken(result.sessionToken)
        setLoginEmail(formData.email)
        setQuickLoginMode(false)  // Hide quick login screen
        setShowSecurityModal(true)  // Show 2FA modal immediately
      } else {
        // Shouldn't happen for 2FA users, but handle gracefully
        navigate('/dashboard')
      }
    } catch (err) {
      // Error will be handled by AuthContext
      // Keep user on quick login screen to retry
    }
  }
  
  const handleCancelQuickLogin = () => {
    setQuickLoginMode(false)
    setQuickLoginAccount(null)
    setFormData(prev => ({ ...prev, password: '' }))
    clearError()
  }

  const handleRemoveRecentAccount = (email: string, e: React.MouseEvent) => {
    e.stopPropagation()
    removeRecentAccount(email)
    const updatedAccounts = getRecentAccounts()
    setRecentAccounts(updatedAccounts)
    
    // If no more recent accounts, show regular login
    if (updatedAccounts.length === 0) {
      setShowRegularLogin(true)
    }
  }

  const handleShowRegularLogin = () => {
    setShowRegularLogin(true)
    clearError()
  }

  const displayErrors = [...validationErrors, ...(error ? [error] : [])]

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white via-gray-50 to-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-200 py-12 px-4 sm:px-6 lg:px-8 relative">

      {/* Back to home button */}
      <a
        href="/"
        className="absolute top-6 left-6 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors duration-200 flex items-center gap-2 group z-10"
      >
        <svg className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span className="font-medium">{t('auth.backToHome')}</span>
      </a>

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <div className="flex items-center justify-center mb-6">
            <FlowLogo size="lg" animate={true} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            MortgageFlow.pro
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {isRegisterMode ? t('auth.createAccount') : t('auth.welcomeBack')}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-200 dark:border-gray-700 p-8">
          {/* Quick Login Password Screen */}
          {quickLoginMode && quickLoginAccount ? (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t('auth.welcomeBack')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('auth.enterPasswordToContinue') || 'Enter your password to continue'}
                </p>
              </div>

              {/* Account Preview */}
              <div className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  {quickLoginAccount.profilePictureUrl ? (
                    <img
                      src={quickLoginAccount.profilePictureUrl}
                      alt={`${quickLoginAccount.firstName} ${quickLoginAccount.lastName}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-center">
                      <span className="text-white font-semibold text-lg">
                        {quickLoginAccount.firstName?.[0] || quickLoginAccount.email[0].toUpperCase()}
                        {quickLoginAccount.lastName?.[0] || ''}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {quickLoginAccount.firstName && quickLoginAccount.lastName 
                      ? `${quickLoginAccount.firstName} ${quickLoginAccount.lastName}`
                      : quickLoginAccount.email
                    }
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {quickLoginAccount.email}
                  </p>
                  {quickLoginAccount.preferredLoginMethod === 'sms_2fa' && (
                    <div className="flex items-center mt-1">
                      <svg className="w-3 h-3 mr-1 text-blue-500" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      <span className="text-xs text-blue-600 dark:text-blue-400">{t('auth.sms2faEnabled')}</span>
                    </div>
                  )}
                  {quickLoginAccount.preferredLoginMethod === 'totp_2fa' && (
                    <div className="flex items-center mt-1">
                      <svg className="w-3 h-3 mr-1 text-purple-500" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-xs text-purple-600 dark:text-purple-400">{t('auth.authAppEnabled')}</span>
                    </div>
                  )}
                </div>
              </div>

              {displayErrors.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl">
                  {displayErrors.map((err, index) => (
                    <div key={index}>{err}</div>
                  ))}
                </div>
              )}

              {/* Password Form */}
              <form className="space-y-4" onSubmit={handleQuickLoginSubmit}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('auth.password')}
                  </label>
                  <input 
                    type="password"
                    name="password"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent transition-all duration-200"
                    placeholder={t('auth.password')}
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    autoFocus
                  />
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 py-3 px-4 rounded-xl font-semibold shadow-lg hover:bg-gray-800 dark:hover:bg-gray-200 hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  disabled={loading}
                >
                  {loading ? t('common.loading') : t('common.continue') || 'Continue'}
                </button>
              </form>

              {/* Cancel / Back button */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleCancelQuickLogin}
                  className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 font-medium transition-colors duration-200 flex items-center justify-center mx-auto"
                  disabled={loading}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  {t('auth.backToRecentAccounts')}
                </button>
              </div>
            </div>
          ) : !showRegularLogin && recentAccounts.length > 0 ? (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t('auth.recentlyLoggedIn')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('auth.clickToContinue')}
                </p>
              </div>

              {displayErrors.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl">
                  {displayErrors.map((err, index) => (
                    <div key={index}>{err}</div>
                  ))}
                </div>
              )}

              <div className="space-y-3">
                {recentAccounts.map((account) => (
                  <button
                    key={account.email}
                    type="button"
                    onClick={() => handleRecentAccountLogin(account)}
                    disabled={loading}
                    className="w-full flex items-center space-x-4 p-4 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
                  >
                    {/* Profile Picture */}
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      {account.profilePictureUrl ? (
                        <img
                          src={account.profilePictureUrl}
                          alt={`${account.firstName} ${account.lastName}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-center">
                          <span className="text-white font-semibold text-lg">
                            {account.firstName?.[0] || account.email[0].toUpperCase()}
                            {account.lastName?.[0] || ''}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Account Info */}
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {account.firstName && account.lastName 
                          ? `${account.firstName} ${account.lastName}`
                          : account.email
                        }
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {account.email}
                      </p>
                      {/* Show badge based on preferred login method */}
                      {(account.preferredLoginMethod || (account.hasPasskey && 'passkey')) && (
                        <div className="flex items-center mt-1">
                          {(account.preferredLoginMethod === 'passkey' || (!account.preferredLoginMethod && account.hasPasskey)) && (
                            <>
                              <svg className="w-3 h-3 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H4v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                              </svg>
                              <span className="text-xs text-green-600 dark:text-green-400">{t('auth.passkeyEnabled')}</span>
                            </>
                          )}
                          {account.preferredLoginMethod === 'sms_2fa' && (
                            <>
                              <svg className="w-3 h-3 mr-1 text-blue-500" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                              </svg>
                              <span className="text-xs text-blue-600 dark:text-blue-400">{t('auth.sms2faEnabled')}</span>
                            </>
                          )}
                          {account.preferredLoginMethod === 'totp_2fa' && (
                            <>
                              <svg className="w-3 h-3 mr-1 text-purple-500" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              <span className="text-xs text-purple-600 dark:text-purple-400">{t('auth.authAppEnabled')}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={(e) => handleRemoveRecentAccount(account.email, e)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
                      title="Remove from recent accounts"
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>

                    {/* Loading indicator */}
                    {loading && (
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                    )}
                  </button>
                ))}
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={handleShowRegularLogin}
                  className="w-full text-center text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 font-medium transition-colors duration-200"
                  disabled={loading}
                >
                  {t('auth.loginDifferentAccount')}
                </button>
              </div>
            </div>
          ) : (
            /* Regular Login Form */
            <form className="space-y-6" onSubmit={isRegisterMode ? handleRegister : handleLogin}>
            {displayErrors.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl">
                {displayErrors.map((err, index) => (
                  <div key={index}>{err}</div>
                ))}
              </div>
            )}

            {isRegisterMode && (
              <>
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('auth.firstName')}
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent transition-all duration-200"
                    placeholder={t('auth.firstName')}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('auth.lastName')}
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent transition-all duration-200"
                    placeholder={t('auth.lastName')}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('common.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent transition-all duration-200"
                    placeholder={t('common.phone')}
                  />
                </div>
                <div>
                  <label htmlFor="language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('auth.selectLanguage')}
                  </label>
                  <select
                    id="language"
                    name="language"
                    value={formData.language}
                    onChange={(e) => {
                      handleInputChange(e as any)
                      i18n.changeLanguage(e.target.value)
                    }}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent transition-all duration-200"
                  >
                    <option value="en">{t('auth.english')}</option>
                    <option value="fr">{t('auth.french')}</option>
                  </select>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.email')}
              </label>
              <input 
                type="email"
                name="email"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent transition-all duration-200"
                placeholder={t('auth.email')}
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.password')}
              </label>
              <input 
                type="password"
                name="password"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent transition-all duration-200"
                placeholder={t('auth.password')}
                value={formData.password}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>

            {isRegisterMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('auth.confirmPassword')}
                </label>
                <input 
                  type="password"
                  name="confirmPassword"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent transition-all duration-200"
                  placeholder={t('auth.confirmPassword')}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </div>
            )}

            <button 
              type="submit" 
              className="w-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 py-3 px-4 rounded-xl font-semibold shadow-lg hover:bg-gray-800 dark:hover:bg-gray-200 hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              disabled={loading}
            >
              {loading ? t('common.loading') : (isRegisterMode ? t('auth.registerButton') : t('auth.loginButton'))}
            </button>
          </form>
          )}

          {/* Passkey Login Section */}
          {!isRegisterMode && supportsPasskeys && (
            <div className="mt-6">
              <div className="text-center">
                <div className="flex items-center justify-center">
                  <div className="border-t border-gray-300 dark:border-gray-600 flex-grow"></div>
                  <span className="px-4 text-sm text-gray-500 dark:text-gray-400">or</span>
                  <div className="border-t border-gray-300 dark:border-gray-600 flex-grow"></div>
                </div>
                <button
                  type="button"
                  onClick={handlePasskeyLogin}
                  className="mt-4 w-full flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-sm"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-3"></div>
                  ) : (
                    <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  )}
                  {loading ? t('auth.authenticating') : t('auth.signInWithPasskey')}
                </button>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {t('auth.choosePasskeys')}
                </p>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={toggleMode}
              className="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 font-medium transition-colors duration-200"
              disabled={loading}
            >
              {isRegisterMode ? t('auth.alreadyHaveAccount') + ' ' + t('auth.signInHere') : t('auth.dontHaveAccount') + ' ' + t('auth.signUpHere')}
            </button>
          </div>

          {/* Back to recent accounts button */}
          {showRegularLogin && recentAccounts.length > 0 && !isRegisterMode && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => {
                  setShowRegularLogin(false)
                  clearError()
                }}
                className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 font-medium transition-colors duration-200 flex items-center justify-center mx-auto"
                disabled={loading}
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('auth.backToRecentAccounts')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Security Verification Modal */}
      <SecurityVerificationModal
        isOpen={showSecurityModal}
        onVerified={handleSecurityVerified}
        onSkip={sessionToken ? undefined : handleSecuritySkip}  // No skip option if sessionToken present (mandatory verification)
        onCancel={sessionToken ? () => {
          // Cancel login - clear session and close modal
          sessionStorage.removeItem('pendingAuthSession')
          setShowSecurityModal(false)
          setSessionToken('')
          clearError()
        } : undefined}
        userEmail={loginEmail}
        sessionToken={sessionToken}
        preferredMethod={preferredMethod}
        has2FA={(() => {
          if (sessionToken) {
            const pendingSession = sessionStorage.getItem('pendingAuthSession')
            if (pendingSession) {
              return JSON.parse(pendingSession).has2FA
            }
          }
          return undefined
        })()}
        hasSMS2FA={(() => {
          if (sessionToken) {
            const pendingSession = sessionStorage.getItem('pendingAuthSession')
            if (pendingSession) {
              return JSON.parse(pendingSession).hasSMS2FA
            }
          }
          return undefined
        })()}
        hasPasskeys={(() => {
          if (sessionToken) {
            const pendingSession = sessionStorage.getItem('pendingAuthSession')
            if (pendingSession) {
              return JSON.parse(pendingSession).hasPasskeys
            }
          }
          return undefined
        })()}
      />
    </div>
  )
}

export default Login 